import React from 'react';
import type { FC } from 'react';
import { match } from 'variant';
import type { DeploymentTopologyEnvironmentType } from '@wix/cli-astro-definitions';
import { useProjectModel } from '../../model.js';
import { ActionStatus } from './ActionStatus.js';
import { useCreatePreview } from './hooks/useCreatePreview.js';
import type { PreviewResult } from './hooks/useCreatePreview.js';
import { SiteTopologyAndLinks } from './SiteTopologyAndLinks.js';
import { AppTopologyAndLinks } from './AppTopologyAndLinks.js';

export type { PreviewResult } from './hooks/useCreatePreview.js';

export const Preview: FC<{
  environment: DeploymentTopologyEnvironmentType;
  baseUrl?: string;
  label?: string;
  previewId?: string;
  onPreviewCreated?: (result: PreviewResult) => void;
}> = ({ environment, baseUrl, label, previewId, onPreviewCreated }) => {
  const { model } = useProjectModel();

  const { uploadStatus, createPreviewStatus, status } = useCreatePreview({
    baseUrl,
    label,
    previewId,
  });

  return (
    <>
      <ActionStatus
        status={uploadStatus}
        loadingText={'Uploading your application files...'}
        successText={'Uploaded successfully!'}
      />
      <ActionStatus
        status={createPreviewStatus}
        loadingText={'Creating a preview...'}
        successText={'Preview created successfully!'}
      />
      {match(status, {
        Error: () => null,
        Loading: () => null,
        Success: ({ result: { previewId, previewUrl, appManifest } }) =>
          model.config.projectType === 'Site' ? (
            <SiteTopologyAndLinks
              environment={environment}
              previewUrl={previewUrl}
              previewId={previewId}
              onTopologyUpdated={() =>
                onPreviewCreated?.({ previewId, previewUrl, appManifest })
              }
            />
          ) : (
            <AppTopologyAndLinks
              environment={environment}
              previewId={previewId}
              onTopologyUpdated={() =>
                onPreviewCreated?.({ previewId, appManifest })
              }
            />
          ),
      })}
    </>
  );
};
